<template>
  <WdsStack justify-space-between class="table-control-bar" :class="{ fullScreenMode }">
    <WdsStack gap-sm align-center justify-center class="search-filter-input">
      <WdsInput
        v-if="searchArg"
        v-model="search"
        :placeholder="searchArg.description"
        :title="searchArg.description ?? ''"
        :prefix-icon="WdsIconNameEnum.SEARCH"
      />
      <WdsButtonGhost
        class="tw-h-7.5 !tw-mr-2"
        data-test="table-filter-button"
        @click="showFilters = !showFilters"
      >
        <div class="tw-my-auto tw-px-3 table-filter-button">
          <WdsStack gap-sm align-center>
            <WdsIcon name="settings-adjust" :size="16" weight="thin" />
            {{ $t('Filters') }}
            <WdsBadge v-if="numActiveFilters > 0" lg primary>
              {{ numActiveFilters }}
            </WdsBadge>
          </WdsStack>
        </div>
      </WdsButtonGhost>
    </WdsStack>
    <WdsStack gap-sm justify-center align-center>
      <slot name="filters"></slot>
      <div v-if="$slots.filters" class="v-split"></div>
      <WdsText :text="$tc(':count item | :count items', numberOfItems)" />
      <div class="v-split"></div>
      <DataTableSort v-model:sort="sort" :sort-options="orderByArg" />
      <div class="v-split"></div>
      <WdsButtonGhost
        :title="$t('Download table')"
        :aria-label="$t('Download table')"
        @click="$emit('download')"
      >
        <WdsIcon name="download" :size="18" />
      </WdsButtonGhost>
      <div class="v-split"></div>
      <WdsButtonGhost
        :title="$t('Fullscreen')"
        :aria-label="$t('Fullscreen')"
        @click="toggleFullScreen"
      >
        <WdsIcon v-if="!fullScreenMode" name="maximize" :size="18" />
        <WdsIcon v-else name="minimize" :size="18" />
      </WdsButtonGhost>
    </WdsStack>
  </WdsStack>
</template>

<script lang="ts" setup>
import {
  WdsButtonGhost,
  WdsIcon,
  WdsInput,
  WdsStack,
  WdsText,
  WdsIconNameEnum,
  WdsBadge,
} from '@worksome/wds-components'
import { print, TypedQueryDocumentNode } from 'graphql'
import { computed, ref, useSlots, watch } from 'vue'
import { useIntrospectionTypes, useQueryType } from '@/components/GraphqlDataTable/composables'
import { $t, $tc } from '@/composables/translation'
import DataTableSort from '@/components/BaseDataTable/DataTableSort.vue'
import { getFieldTypeLeaf } from '@/components/GraphqlDataTable/tools'
import { convertEnumToDisplayText } from '@/utils/tools'
import { SortOption } from '@/components/BaseDataTable/composables/dataTableSort'
import { SortOrder } from '@/gql/graphql'
import columns from '@/components/GraphqlDataTable/columns'
import { InputValueFragmentDef, TypeRefFragmentDef } from '@/components/GraphqlDataTable/queries'
import { useFragment } from '@/gql'

const clientId = 'publicApiCustomFields'
const slots = useSlots()
const emit = defineEmits(['sort-settings', 'download', 'full-screen'])

interface DataTableControlBarProps {
  query: TypedQueryDocumentNode
  numberOfItems: number
  numActiveFilters: number
}

const props = defineProps<DataTableControlBarProps>()
const stringifiedQuery = computed(() => print(props.query))

const { args: queryArgs } = useQueryType(stringifiedQuery)

const { typeLookup } = useIntrospectionTypes()

const searchArg = computed(() =>
  queryArgs.value.inputtedArgs.filter((arg) => !!arg).find(({ name }) => name === 'search'),
)

const fullScreenMode = ref(false)
const toggleFullScreen = () => {
  fullScreenMode.value = !fullScreenMode.value
  emit('full-screen', fullScreenMode.value)
}

const INPUT_OBJECT = 'INPUT_OBJECT'

// Define the EnumType interface
interface EnumType {
  name: string
  description?: string | null
}

// Update the guessEnumComplimentaryText function
const guessEnumComplimentaryText = (enumValue: EnumType, sort: SortOrder): string => {
  // Extract and normalize the name and description
  const name = (enumValue.name || '').toLowerCase().replace(/_/g, ' ')
  const description = (enumValue.description || '').toLowerCase()

  // Combine name and description for keyword matching
  const combinedText = `${name} ${description}`

  // Define keyword arrays for each type
  const dateKeywords = ['date', 'time', 'start date', 'end date']
  const stringKeywords = ['name', 'company', 'worker', 'job', 'title']
  const numericKeywords = ['number', 'amount', 'quantity', 'rate']

  // Function to calculate score for each type
  const calculateScore = (keywords: string[]): number => {
    let score = 0
    keywords.forEach((keyword) => {
      // Use a regular expression to count keyword occurrences
      const regex = new RegExp(`\\b${keyword}\\b`, 'g')
      const matches = combinedText.match(regex)
      if (matches) {
        score += matches.length
      }
    })
    return score
  }

  // Create an array of type-score pairs
  const scores = [
    {
      // Date type
      sortText: sort === SortOrder.Asc ? $t('Old to new') : $t('New to old'),
      score: calculateScore(dateKeywords),
    },
    {
      // String type
      sortText: sort === SortOrder.Asc ? $t('A - Z') : $t('Z - A'),
      score: calculateScore(stringKeywords),
    },
    {
      // Numeric type
      sortText: sort === SortOrder.Asc ? $t('Less - More') : $t('More - Less'),
      score: calculateScore(numericKeywords),
    },
  ]

  // Use reduce to find the type with the highest score
  const maxScoreType = scores.reduce((max, curr) => (curr.score > max.score ? curr : max), {
    sortText: sort === SortOrder.Asc ? $t('Ascending') : $t('Descending'),
    score: 0,
  })

  // Determine the type
  return maxScoreType.sortText
}

const existingSorts = computed(() =>
  Object.values(columns)
    .filter(({ sort }) => !!sort)
    .flatMap(({ sort }) => sort)
    .reduce((acc, sort) => ({ ...acc, [sort?.name ?? 'no-name']: sort }), {}),
)

const orderByArg = computed(() => {
  const OBArg = queryArgs.value.inputtedArgs
    .filter((arg) => !!arg)
    .find(({ name }) => name === 'orderBy')

  if (!OBArg || !OBArg.type) return []

  const leaf = getFieldTypeLeaf(useFragment(TypeRefFragmentDef, OBArg.type))
  if (!leaf) return []
  const type = typeLookup.value[leaf.name ?? '']

  // Should be an input type
  const inputFields = useFragment(InputValueFragmentDef, type.inputFields)
  if (inputFields === undefined || inputFields === null) return []
  if (type.kind !== INPUT_OBJECT && inputFields.length > 0) return []
  // We are assuming that our input object is on the type
  const field = inputFields.find((f) => f.name === 'field')
  if (!field) return []
  const fieldLeaf = getFieldTypeLeaf(useFragment(TypeRefFragmentDef, field.type))
  if (!fieldLeaf) return []

  const orderTypes = typeLookup.value[fieldLeaf.name ?? '']
  if (!orderTypes || !orderTypes.enumValues) return []

  const sortOptions: SortOption[] = orderTypes.enumValues.flatMap(({ name, description }) =>
    [SortOrder.Asc, SortOrder.Desc].flatMap((sortOrder) => {
      const key = `${name}_${sortOrder}`
      if (existingSorts.value[key]) {
        return existingSorts.value[key]
      }
      return {
        name: key,
        primaryText: convertEnumToDisplayText(name),
        secondaryText: guessEnumComplimentaryText({ name, description }, sortOrder),
        orderByColumn: name,
        sortOrder,
        title: $t(description),
      }
    }),
  )

  // Sort order text
  // Date type, New to old | Old to new
  // String type, A-Z | Z-A
  // Numeric type, More - Less | Less - More

  return sortOptions
})

const search = defineModel<string>('search')
const showFilters = defineModel<boolean>('showFilters')
const sort = ref<SortOption | undefined>(undefined)
const orderBy = defineModel<{ field: string; order: SortOrder | null } | undefined>('orderBy')

watch(sort, () => {
  if (!sort.value) {
    orderBy.value = undefined
    return
  }

  // Convert the sort value to a type that supports the input value for our query
  orderBy.value = {
    field: sort.value.orderByColumn,
    order: sort.value.sortOrder,
  }
})

watch([orderBy, orderByArg], () => {
  sort.value = orderByArg.value.find(
    (option) =>
      option.orderByColumn === orderBy.value?.field && orderBy.value?.order === option.sortOrder,
  )
})
</script>

<style lang="scss" scoped>
.fullScreenMode {
  padding: 20px;
}

.field-table {
  margin-top: 3rem;
  &:deep(.table-wrap) {
    max-height: 300px;
    overflow: auto;
  }
}

.query-table {
  margin-top: 3rem;
  &:deep(.table-wrap) {
    overflow: auto;
  }
}

.v-split {
  border-left: 1px solid #c1c1cb;
  height: 16px;
}

.table-control-bar {
  &:deep {
    .wds-input-wrapper {
      input {
        text-overflow: ellipsis;
      }
    }
  }
}

.table-filter-button {
  &:deep {
    .wds-icon {
      path {
        stroke: currentColor;
        stroke-width: 1px;
      }
    }
  }
}

.search-filter-input {
  &:deep {
    .wds-input-label {
      margin: 0;
    }
  }
}
</style>
